import { path } from "../navigation/path";

/**
 * Permission mapping for routes and menu items
 * Maps each route/menu item to the required permission(s)
 * If user doesn't have the permission, they cannot access it
 * null means Super Admin only (checked via allowedRoles in route)
 */
export const routePermissions: Record<string, string | string[] | null> = {
  // Dashboard - everyone with dashboard.view or any permission can access
  "/": "dashboard.view",

  // User Management
  [path.USERS]: "user.manage",
  
  // Role Management
  [path.ROLES]: "role.manage",

  // Zone Management
  [path.ZONES]: "zone.view", // View permission allows access to zone page

  // Group Management
  [path.GROUPS]: "group.view",

  // Team Management
  [path.TEAMS]: "team.view",

  // Member Management
  [path.MEMBERS]: "member.view",
  [path.MEMBER_DETAILS]: "member.view",

  // Vehicle Management
  [path.VEHICLES]: "vehicle.view",
  [path.STREAM_VEHICLE]: "vehicle.stream",

  // Visitor Management
  [path.VISITORS]: "visitor.view",
  [path.VISITOR_DETAILS]: "visitor.view",

  // Messaging
  [path.MESSAGING]: "messaging.compose",
  [path.MESSAGE_TRANSACTIONS]: "messaging.view",
  [path.MESSAGE_ANNIVERSARIES]: "messaging.view",
  [path.OCCASION_VERSES]: "messaging.view",

  // Admin
  [path.CONFIG]: "config.manage",
  [path.REQUESTS]: "request.manage",
  [path.CAMERAS]: "camera.manage",
  [path.NODES]: "node.manage",
  [path.AUDIT]: "audit.view",
  [path.LOCATIONS]: "location.manage",
  [path.BIBLE_MANAGEMENT]: "bible.manage",
  [path.MASTER_DATA]: null, // Super Admin only - checked via allowedRoles
};

/**
 * Menu item permissions
 * Maps menu items to required permissions
 */
export const menuItemPermissions: Record<string, string | string[]> = {
  // Messaging submenu
  "messaging.compose": "messaging.compose",
  "messaging.transactions": "messaging.view",
  "messaging.anniversaries": "messaging.view",
  "messaging.bible-verses": "messaging.view",

  // Members submenu
  "members.list": "member.view",
  "members.zones": "zone.view",
  "members.groups": "group.view",
  "members.teams": "team.view",

  // Vehicles submenu
  "vehicles.live": "vehicle.stream",
  "vehicles.search": "vehicle.view",

  // Admin submenu
  "admin.config": "config.manage",
  "admin.users": "user.manage",
  "admin.approvals": "request.manage",
  "admin.camera": "camera.manage",
  "admin.nodes": "node.manage",
  "admin.audit": "audit.view",
  "admin.locations": "location.manage",
  "admin.bible": "bible.manage",
  "admin.roles": "role.manage",

  // Visitors submenu
  "visitor.list": "visitor.view",
};

/**
 * Check if user has permission for a route
 * @param userPermissions - Array of user's permissions
 * @param requiredPermission - Required permission(s) - can be string or array
 * @param isSuperAdmin - Whether user is Super Admin
 */
export const hasRoutePermission = (
  userPermissions: string[],
  requiredPermission: string | string[],
  isSuperAdmin: boolean
): boolean => {
  // Super Admin has access to everything
  if (isSuperAdmin) {
    return true;
  }

  // Check for "admin" permission (users with admin permission have all permissions)
  if (userPermissions.includes("admin")) {
    return true;
  }

  // If requiredPermission is an array, user needs at least one
  if (Array.isArray(requiredPermission)) {
    return requiredPermission.some(perm => 
      userPermissions.includes(perm) ||
      // Check for .manage permission (e.g., zone.manage includes zone.view)
      userPermissions.some(userPerm => {
        const basePerm = perm.split('.')[0];
        return userPerm === `${basePerm}.manage` || userPerm.startsWith(`${basePerm}.`);
      })
    );
  }

  // Single permission check
  if (userPermissions.includes(requiredPermission)) {
    return true;
  }

  // Check for .manage permission (e.g., zone.manage includes zone.view)
  const basePermission = requiredPermission.split('.')[0];
  return userPermissions.some(perm => 
    perm === `${basePermission}.manage` || 
    perm.startsWith(`${basePermission}.`)
  );
};

/**
 * Get required permission for a route
 */
export const getRoutePermission = (routePath: string): string | string[] | null => {
  return routePermissions[routePath] || null;
};
